/**
 * relies on itentity equality, replaces an element in an array
 **/
export const replace = <T>(
    ary: T[],
    item: T,
    replacement: T,
    cmp?: (a: T, b: T) => boolean
): T[] => {
    return ary.map((member) => {
        if (cmp ? cmp(member, item) : member === item) {
            return replacement;
        }
        return member;
    });
};

export const keyedIndex = <T, K>(ary: readonly T[], kf: (t: T) => K): Map<K, T> => {
    const rev: Map<K, T> = new Map();

    for (const member of ary) {
        rev.set(kf(member), member);
    }

    return rev;
};

export const reverseIndex = <T>(ary: readonly T[]): Map<T, number> => {
    const rev: Map<T, number> = new Map();

    for (let i = 0; i < ary.length; i++) {
        rev.set(ary[i], i);
    }
    return rev;
};

export const pick = <T>(array: T[]): T => {
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
};

/**
 * Zips together two arrays into an array of tuples.
 *
 * @template T The type of elements in the first array.
 * @template U The type of elements in the second array.
 * @param {T[]} arr1 The first array to be zipped.
 * @param {U[]} arr2 The second array to be zipped.
 * @returns {Array<[T, U]>} An array of tuples, where each tuple contains an element from `arr1` and an element from `arr2`.
 *
 * @example
 * const names = ['Alice', 'Bob', 'Charlie'];
 * const ages = [25, 30, 35];
 * const zipped = zip(names, ages);
 * console.log(zipped); // [['Alice', 25], ['Bob', 30], ['Charlie', 35]]
 *
 * @remarks
 * - If `arr1` and `arr2` are of different lengths, the resulting array will have the length of the shortest input array, and extra elements from the longer array will be ignored.
 * - If either of `arr1` or `arr2` is empty, the resulting array will also be empty.
 */
export const zip = <T, U>(arr1: T[], arr2: U[]): Array<[T, U]> => {
    const minLength = Math.min(arr1.length, arr2.length);
    return arr1.slice(0, minLength).map((item, index) => [item, arr2[index]]);
};

/**
 * Unzips an array of tuples into two separate arrays.
 *
 * @template T The type of the first element in each tuple.
 * @template U The type of the second element in each tuple.
 * @param {Array<[T, U]>} arr The array of tuples to be unzipped.
 * @returns {[T[], U[]]} A tuple containing two arrays: the first array contains the first elements of the input tuples, and the second array contains the second elements.
 *
 * @example
 * const zipped = [['Alice', 25], ['Bob', 30], ['Charlie', 35]];
 * const [names, ages] = unzip(zipped);
 * console.log(names); // ['Alice', 'Bob', 'Charlie']
 * console.log(ages);  // [25, 30, 35]
 *
 * @remarks
 * - If `arr` is empty, the resulting arrays will also be empty.
 * - If any of the tuples in `arr` are not of length 2, the function may exhibit unexpected behavior.
 */
export const unzip = <T, U>(arr: Array<[T, U]>): [T[], U[]] => {
    const arr1: T[] = [];
    const arr2: U[] = [];

    for (const [item1, item2] of arr) {
        arr1.push(item1);
        arr2.push(item2);
    }

    return [arr1, arr2];
};

export const sortByReference = <T extends { client_id: any }>(
    targetArray: T[],
    referenceArray: T[]
): T[] => {
    const order: { [key: string]: number } = {};

    // Create a mapping of client_id to its index in the referenceArray
    referenceArray.forEach((item, index) => {
        order[item.client_id] = index;
    });

    // Sort the targetArray based on the order in the referenceArray
    return targetArray.sort((a, b) => {
        const orderA = order[a.client_id] !== undefined ? order[a.client_id] : Infinity;
        const orderB = order[b.client_id] !== undefined ? order[b.client_id] : Infinity;
        return orderA - orderB;
    });
};
